import { useCallback, useRef } from "react"
import { Box, Stack, styled, SxProps, Theme, Tooltip } from "@mui/material"
import { FastCalculatorDefaultArticleItemState, FastCalculatorOeArticleItemState } from "@tm/models"
import { useLocalization } from "@tm/localization"
import { Typography } from "../../../../muiStyled/DataDisplay/typographie"
import { AmountField } from "../../../../muiStyled/Inputs/textfield"
import { useArticleItemState } from "../../ContextProviderStates"

type Props = {
    inputSx?: SxProps<Theme>
    componentSx?: SxProps<Theme>
}

export function PriceInputField({ inputSx, componentSx }: Props) {
    const debounceRef = useRef<NodeJS.Timeout | null>(null)
    const { translateText, formatCurrency } = useLocalization()
    const handlePriceChange = useArticleItemState(
        (x: FastCalculatorDefaultArticleItemState | FastCalculatorOeArticleItemState) => x.handlePriceChange
    )
    const currencyCode = useArticleItemState((x: FastCalculatorDefaultArticleItemState | FastCalculatorOeArticleItemState) => x.currencyCode)
    const articlePrice = useArticleItemState((x: FastCalculatorDefaultArticleItemState | FastCalculatorOeArticleItemState) => x.articlePrice)
    const articleQuantity = useArticleItemState((x: FastCalculatorDefaultArticleItemState | FastCalculatorOeArticleItemState) => x.articleQuantity)
    const type = useArticleItemState((x: FastCalculatorDefaultArticleItemState | FastCalculatorOeArticleItemState) => x.type)
    const showPriceInputField = useArticleItemState(
        (x: FastCalculatorDefaultArticleItemState | FastCalculatorDefaultArticleItemState) => x.showPriceInputField
    )

    const debouncedChange = useCallback(
        (value: number) => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current)
            }
            debounceRef.current = setTimeout(() => {
                handlePriceChange?.(value)
            }, 600)
        },
        [handlePriceChange]
    )

    const isInvalid = articlePrice == 0

    return (
        <Box display="flex" flexDirection="row" style={{ margin: "0" }} sx={componentSx}>
            {showPriceInputField && (
                <Box sx={inputSx}>
                    <Typography variant="body2">{currencyCode}</Typography>
                    <AmountField
                        required
                        min={0}
                        max={9999}
                        step={0.01}
                        value={articlePrice}
                        hideArrows
                        fullWidth
                        onChange={(value: number | undefined, _event?: React.SyntheticEvent<HTMLInputElement>) => {
                            if (typeof value === "number" && !Number.isNaN(value)) {
                                debouncedChange(value)
                            }
                        }}
                        error={isInvalid}
                    />
                </Box>
            )}
            <Stack
                flexDirection="row"
                alignItems="center"
                height="100%"
                width={type === "OE" ? "100%" : "auto"}
                margin="auto"
                spacing={2}
                justifyContent="center"
                gap="1em"
            >
                <Tooltip
                    title={translateText(2007)}
                    disableHoverListener={type === "OE"}
                    disableFocusListener={type === "OE"}
                    disableTouchListener={type === "OE"}
                >
                    <Typography
                        display="inline"
                        style={
                            type === "default"
                                ? {
                                      marginTop: "16px",
                                      maxWidth: "3em",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      verticalAlign: "bottom",
                                  }
                                : { marginTop: "16px" }
                        }
                    >
                        {translateText(2007)}
                    </Typography>
                </Tooltip>
                <StyledPriceTypography>{formatCurrency((articleQuantity ?? 0) * (articlePrice ?? 0), currencyCode ?? "EUR")}</StyledPriceTypography>
            </Stack>
        </Box>
    )
}

const StyledPriceTypography = styled(
    Typography,
    {}
)(({ theme }) => ({
    textAlign: "right",
    whiteSpace: "nowrap",
    lineHeight: 1.2,
    color: theme.overwrites?.components?.partListV2?.articleItem?.erpContainer?.purchasePriceColor || theme.palette.primary.main,
    fontWeight: "bold",
}))
